import React, { useMemo, useState } from 'react';
import { App, Button, Dropdown, Grid } from 'antd';
import { Link, useNavigate } from 'react-router-dom';

import GroupHeader from '../common/GroupHeader';
import Guidance from '../common/Guidance';
import { buildActionMenu } from '../common/actionMenu';
import DashboardGrid from '../dashboards/DashboardGrid';
import SourceMark from '../notifications/SourceMark';
import RetryDataState from '../common/RetryDataState';
import { DATA_STATE, Skeleton } from '../loading';
import { KEBAB } from '../icons';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import useChartContexts from '../../hooks/useChartContexts';
import useDashboardRun from '../../hooks/useDashboardRun';
import useOverview from '../../hooks/useOverview';
import { OVERVIEW_SOURCE, clearCollectionOverview, setCollectionOverview } from '../../services/overview';
import { FILTER_UNREADABLE, PAUSED, dashboardPath } from '../../utils/dashboards';
import { normalizeId } from '../../utils/ids';
import { canManage, canReadCharts } from '../../utils/permissions';
import OverviewPickerModal, { NO_DASHBOARD, PICK_FOR } from './OverviewPickerModal';

/** The one line a manager sees when no dashboard is chosen (plan §4.6). */
export const OVERVIEW_EMPTY_TEXT = 'Choose a dashboard to show here.';

/** Where the dashboard came from, in the SourceMark's two looks (standards §9 item 9). */
export function overviewMark(overview) {
	if (!overview || !overview.dashboard) return null;
	if (overview.source === OVERVIEW_SOURCE.PERSON) {
		return { word: 'Your choice', phrase: 'You chose this dashboard for your Overview. Everyone else sees the collection\'s.', own: true };
	}
	return { word: 'From the collection', phrase: 'A collection manager chose this dashboard for everyone', own: false };
}

const editChartPath = (collectionId, chartId) => `/collections/${collectionId}/charts/${chartId}/edit`;

/**
 * The Overview's dashboard (charts A7; plan §4.6, §6.6; ruled q04): a
 * collection's front page shows ONE dashboard — the one its manager chose for
 * everyone, or the one the person chose for themselves (the server's cascade:
 * person → collection → none, useOverview; never a collection above's,
 * KPR-1084).
 *
 *   A dashboard   a GroupHeader titled with its name, its SourceMark ("Your
 *                 choice", "From the collection", "From {parent}") and a ⋯:
 *                 Show on my Overview… (pick another), Use the collection's
 *                 Overview when the person has their own, and — a manager's —
 *                 Show on Overview for everyone…; then its tiles at the
 *                 dashboard's default range and filter, in ONE batch run
 *                 after the page has painted (live like the dashboard page:
 *                 refreshing while visible, pausing when idle — paused, it
 *                 says "Paused. Refresh to update." with Refresh, as the
 *                 dashboard page does; a filter the server cannot read is
 *                 said once above the tiles); then **Open dashboard**,
 *                 where the controls live.
 *   None          a reader sees no section; a manager sees one line, "Choose
 *                 a dashboard to show here.", with Choose… and the Reports
 *                 door.
 *
 * Charts are for people who read the collection themselves (ruled d14): a
 * global collection's consumer and a link-only reader see nothing here.
 */
export default function OverviewSection({ collection, collectionId }) {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const favorites = useOptionalFavorites();
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;
	const allowed = Boolean(collection) && canReadCharts(collection);
	const manager = Boolean(collection) && canManage(collection);
	const { overview, status, reload } = useOverview(collectionId, { enabled: allowed, collectionChoice: collection?.overviewDashboard_id });
	const dashboard = overview.dashboard;
	const [picking, setPicking] = useState(null);

	const tiles = useMemo(() => (dashboard && Array.isArray(dashboard.tiles) ? dashboard.tiles : []), [dashboard]);
	const charts = useMemo(() => tiles.filter((t) => t && t.kind === 'chart' && t.available && t.chart).map((t) => t.chart), [tiles]);
	const chartContext = useChartContexts(charts);
	const range = dashboard && dashboard.controls && dashboard.controls.range ? dashboard.controls.range : undefined;
	const filter = dashboard && dashboard.controls && typeof dashboard.controls.filter === 'string' ? dashboard.controls.filter : '';
	const run = useDashboardRun({ tiles, collectionId, range, filter, enabled: Boolean(dashboard) });

	if (!allowed) return null;
	// Until the server has answered, two chart frames where the dashboard will
	// be, on the standard's clock (§14); a failed read says so with Retry. A
	// manager is still not told to choose a dashboard the page could not ask about.
	if (status !== 'ready') {
		return (
			<section className='collection-home__section overview-section overview-section--loading' aria-label='Overview'>
				<RetryDataState
					state={status === 'error' ? DATA_STATE.ERROR : DATA_STATE.LOADING}
					skeleton={<div className='dashboard-page__skeleton'><Skeleton variant='chart-frame' count={2} /></div>}
					error={null}
					onRetry={reload}
					label='the overview'
				/>
			</section>
		);
	}

	const chooseMine = async (id) => {
		if (!favorites || typeof favorites.chooseMyOverview !== 'function') return false;
		return favorites.chooseMyOverview(collectionId, id);
	};
	const chooseForEveryone = async (id) => {
		try {
			if (id === NO_DASHBOARD) await clearCollectionOverview(collectionId);
			else await setCollectionOverview(collectionId, id);
			message.success(id === NO_DASHBOARD ? 'Overview shows no dashboard of this collection\'s own now.' : 'Overview shows this dashboard for everyone now.');
			favorites?.noteOverviewChanged?.();
			reload();
			return true;
		} catch (err) {
			message.error(err?.response?.data?.message || 'Could not change the Overview. Please try again.');
			return false;
		}
	};

	const picker = picking ? (
		<OverviewPickerModal
			open
			mode={picking}
			collectionId={collectionId}
			current={picking === PICK_FOR.EVERYONE ? overview.collectionChoice : overview.mine}
			hasOwnChoice={Boolean(overview.collectionChoice)}
			onChoose={picking === PICK_FOR.EVERYONE ? chooseForEveryone : chooseMine}
			onClose={() => setPicking(null)}
		/>
	) : null;

	if (!dashboard) {
		if (!manager) return picker;
		return (
			<section className='collection-home__section overview-section overview-section--empty' aria-label='Overview dashboard'>
				<p className='overview-section__empty'>
					<span>{OVERVIEW_EMPTY_TEXT}</span>
					<Button type='link' size='small' className='overview-section__choose' onClick={() => setPicking(PICK_FOR.EVERYONE)}>Choose…</Button>
					<Link className='collection-home__more' to={`/collections/${collectionId}/reports`}>Go to Reports</Link>
				</p>
				{picker}
			</section>
		);
	}

	const mark = overviewMark(overview);
	const id = normalizeId(dashboard._id);
	const menu = buildActionMenu([
		{ key: 'open', group: 'navigate', glyph: 'open', label: 'Open dashboard', onClick: () => navigate(dashboardPath(collectionId, id)) },
		{ key: 'mine', group: 'record', glyph: 'home', label: 'Show on my Overview…', hidden: !favorites, onClick: () => setPicking(PICK_FOR.ME) },
		{ key: 'collection', group: 'record', glyph: 'home', label: 'Use the collection\'s Overview', hidden: !favorites || overview.source !== OVERVIEW_SOURCE.PERSON, onClick: () => chooseMine(null) },
		{ key: 'everyone', group: 'record', glyph: 'collection', label: 'Show on Overview for everyone…', hidden: !manager, onClick: () => setPicking(PICK_FOR.EVERYONE) },
	]);

	return (
		<section className='collection-home__section overview-section' aria-label='Overview dashboard' data-dashboard-id={id}>
			<GroupHeader
				label={dashboard.name || 'Dashboard'}
				action={(
					<span className='overview-section__actions'>
						{mark ? <SourceMark word={mark.word} phrase={mark.phrase} own={mark.own} className='overview-section__mark' focusable /> : null}
						{menu ? (
							<Dropdown trigger={['click']} placement='bottomRight' menu={menu}>
								<Button type='text' size='small' className='overview-section__more' icon={<KEBAB />} aria-label='Overview dashboard actions' />
							</Dropdown>
						) : null}
					</span>
				)}
			/>
			{/* The live run stopped on an idle page (useDashboardRun): said, with the way back — never numbers that stop silently (the release review's m1). */}
			{run.paused && !run.loading ? (
				<p className='overview-section__status' role='status'>
					<span>{PAUSED}</span>
					<Button type='link' size='small' className='overview-section__refresh' onClick={run.refresh}>Refresh</Button>
				</p>
			) : null}
			{run.filterError ? (
				<Guidance tone='caution' className='overview-section__filter-error' title={FILTER_UNREADABLE} why={[run.filterError]} />
			) : null}
			<DashboardGrid
				tiles={tiles}
				results={run.results}
				loading={run.loading}
				collectionId={collectionId}
				phone={phone}
				chartContext={chartContext}
				onEditChart={(chart) => navigate(editChartPath(collectionId, normalizeId(chart._id)))}
				onRetry={run.refresh}
				emptyText='Nothing on this dashboard yet.'
			/>
			<Link className='collection-home__more overview-section__open' to={dashboardPath(collectionId, id)}>Open dashboard</Link>
			{picker}
		</section>
	);
}
